import { TypedDocumentNode, gql } from '@apollo/client';
import { useMemo } from 'react';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import LoadingIndicator from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { InstantCardSearch } from '@sorare/core/src/components/search/InstantSearch';

import { usePrimaryBuyAlgoliaParams } from 'hooks/primaryBuy/usePrimaryBuyAlgoliaParams';

import { Content } from './Content';
import { PrimaryBuyGrid_teamInterface } from './__generated__/index.graphql';

type Props = {
  homeTeam: Nullable<PrimaryBuyGrid_teamInterface>;
  awayTeam: Nullable<PrimaryBuyGrid_teamInterface>;
  gameSlug: string;
  isNationalTeam: boolean;
  onPrimaryBuySuccess?: () => void;
  onInfoClick?: (cardSlug: string, playerSlug: string) => void;
  startingLineup?: string[];
};

export const PrimaryBuyGrid = ({
  homeTeam,
  awayTeam,
  gameSlug,
  isNationalTeam,
  onPrimaryBuySuccess,
  onInfoClick,
  startingLineup,
}: Props) => {
  const teamSlugs = [homeTeam?.slug, awayTeam?.slug].filter(Boolean);
  const optionalFilters = useMemo(
    () =>
      (startingLineup?.length && startingLineup.map(s => `player.slug:${s}`)) ||
      undefined,
    [startingLineup]
  );

  const getOptionalFilters = optionalFilters
    ? () => optionalFilters
    : undefined;

  const { sport, facetFilters, loading } = usePrimaryBuyAlgoliaParams({
    teamSlugs,
    isNationalTeam,
    rarity: Rarity.limited,
  });

  if (loading) {
    return <LoadingIndicator fullHeight />;
  }

  return (
    <InstantCardSearch
      indexes={['Cards On Sale Highest Average Score']}
      attributesToRetrieve={[
        'sport',
        'position',
        'player.display_name',
        'player.slug',
      ]}
      sport={sport}
      analyticsTags={[]}
      defaultFilters={[facetFilters]}
      distinct
      defaultHitsPerPage={40}
      getOptionalFilters={getOptionalFilters}
    >
      <Content
        gameSlug={gameSlug}
        onPrimaryBuySuccess={() => {
          onPrimaryBuySuccess?.();
        }}
        isNationalTeam={isNationalTeam}
        onInfoClick={onInfoClick}
        startingLineup={startingLineup}
      />
    </InstantCardSearch>
  );
};

PrimaryBuyGrid.fragments = {
  teamInterface: gql`
    fragment PrimaryBuyGrid_teamInterface on TeamInterface {
      slug
      name
      code
      pictureUrl
    }
  ` as TypedDocumentNode<PrimaryBuyGrid_teamInterface>,
};
